import { getAttributeValues } from '@gonfalon/openapi/operations/getAttributeValues';
import { getExperiment } from '@gonfalon/openapi/operations/getExperiment';
import { type GetExperimentQueryParams } from '@gonfalon/openapi/operations/getExperiment';
import type { GetExperimentAnalysisQueryParams } from '@gonfalon/openapi/operations/getExperimentAnalysis';
import { getExperimentAnalysis } from '@gonfalon/openapi/operations/getExperimentAnalysis';
import { getExperimentationSettings } from '@gonfalon/openapi/operations/getExperimentationSettings';
import { getExperimentResultsLastUpdated } from '@gonfalon/openapi/operations/getExperimentResultsLastUpdated';
import { getExperiments } from '@gonfalon/openapi/operations/getExperiments';
import {
  getExperimentsAnyEnv,
  type GetExperimentsAnyEnvQueryParams,
} from '@gonfalon/openapi/operations/getExperimentsAnyEnv';
import { getExperimentTraffic } from '@gonfalon/openapi/operations/getExperimentTraffic';
import {
  getQuantileExperimentResults,
  type GetQuantileExperimentResultsQueryParams,
} from '@gonfalon/openapi/operations/getQuantileExperimentResults';
import {
  getSegmentsOfInterest,
  type GetSegmentsOfInterestQueryParams,
} from '@gonfalon/openapi/operations/getSegmentsOfInterest';
import { internalDeleteExperimentStratifiedSampling } from '@gonfalon/openapi/operations/internalDeleteExperimentStratifiedSampling';
import { internalGetExperimentStratifiedSampling } from '@gonfalon/openapi/operations/internalGetExperimentStratifiedSampling';
import { internalGetIterationContextAttributes } from '@gonfalon/openapi/operations/internalGetIterationContextAttributes';
import { internalGetIterationExposures } from '@gonfalon/openapi/operations/internalGetIterationExposures';
import { internalGetIterationImage } from '@gonfalon/openapi/operations/internalGetIterationImage';
import { internalPostExperimentStratifiedSampling } from '@gonfalon/openapi/operations/internalPostExperimentStratifiedSampling';
import { postAuditLogEntries } from '@gonfalon/openapi/operations/postAuditLogEntries';
import { putExperimentationSettings } from '@gonfalon/openapi/operations/putExperimentationSettings';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { createQueryKeys } from '@lukemorales/query-key-factory';

import { createInfiniteQueryOptions } from '../internal/createInfiniteQueryOptions';
import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';
import { reactQueryResponseAdapter } from '../internal/reactQueryResponseAdapter';

/**
 * @deprecated Use `createQueryOptions` with the OpenAPI operation directly instead.
 * See packages/rest-api/README.md for the recommended pattern.
 */
export const experiments = createQueryKeys('experiments', {
  anyEnv: ({ projectKey, params }: { projectKey: string; params?: GetExperimentsAnyEnvQueryParams }) => ({
    queryKey: params ? [projectKey, params] : [projectKey],
    queryFn: async () => reactQueryResponseAdapter(getExperimentsAnyEnv({ projectKey, query: params })),
  }),
  detail: ({
    projectKey,
    environmentKey,
    experimentKey,
    params,
  }: {
    projectKey: string;
    environmentKey: string;
    experimentKey: string;
    params?: GetExperimentQueryParams;
  }) => ({
    queryKey: params
      ? [projectKey, environmentKey, experimentKey, params]
      : [projectKey, environmentKey, experimentKey],
    contextQueries: {
      percentileResults: ({
        metricKey,
        params: percentileResultsParams,
      }: {
        metricKey: string;
        params?: GetQuantileExperimentResultsQueryParams;
      }) => ({
        queryKey: params ? [metricKey, params] : [metricKey],
        queryFn: async () =>
          reactQueryResponseAdapter(
            getQuantileExperimentResults({
              projectKey,
              environmentKey,
              experimentKey,
              metricKey,
              query: percentileResultsParams,
            }),
          ),
      }),
      analysis: ({
        metricKey,
        params: analysisParams,
      }: {
        metricKey: string;
        params?: GetExperimentAnalysisQueryParams;
      }) => ({
        queryKey: analysisParams ? [metricKey, analysisParams] : [metricKey],
        queryFn: async () =>
          reactQueryResponseAdapter(
            getExperimentAnalysis({ projectKey, environmentKey, experimentKey, metricKey, query: analysisParams }),
          ),
      }),
      segmentsOfInterest: ({
        metricKey,
        params: segmentsOfInterestParams,
      }: {
        metricKey: string;
        params: GetSegmentsOfInterestQueryParams;
      }) => ({
        queryKey: segmentsOfInterestParams ? [metricKey, segmentsOfInterestParams] : [metricKey],
        queryFn: async () =>
          reactQueryResponseAdapter(
            getSegmentsOfInterest({
              projectKey,
              environmentKey,
              experimentKey,
              metricKey,
              query: segmentsOfInterestParams,
            }),
          ),
      }),
      traffic: ({ iterationId }: { iterationId: string }) => ({
        queryKey: [iterationId],
        queryFn: async () =>
          reactQueryResponseAdapter(getExperimentTraffic({ projectKey, environmentKey, experimentKey, iterationId })),
      }),
    },
  }),
});

export const experimentDetail = experiments.detail;

export const getExperimentsQuery = createQueryOptions(getExperiments);

export const getExperimentsAnyEnvQuery = createQueryOptions(getExperimentsAnyEnv);

export const getExperimentQuery = createQueryOptions(getExperiment);

export const getExperimentTrafficQuery = createQueryOptions(getExperimentTraffic);

export const getExperimentResultsLastUpdatedQuery = createQueryOptions(getExperimentResultsLastUpdated);

export const getExperimentationSettingsQuery = createQueryOptions(getExperimentationSettings);

/**
 * Replace a project's experimentation settings (including its randomization units).
 * This is a whole-object PUT: compute the desired settings from the current cached
 * value in the caller, then pass the full payload here. Invalidates the settings
 * query on success.
 */
export const usePutExperimentationSettings = createMutationHook(putExperimentationSettings, {
  onSuccess: (_data, { projectKey }) => {
    void getQueryClient().invalidateQueries({ queryKey: getExperimentationSettingsQuery({ projectKey }).queryKey });
  },
});

export const getExperimentAttributeValuesQuery = createQueryOptions(getAttributeValues);

export const getIterationContextAttributesQuery = createQueryOptions(internalGetIterationContextAttributes);

export const getIterationExposuresQuery = createQueryOptions(internalGetIterationExposures);

export const getIterationImagesQuery = createQueryOptions(internalGetIterationImage);

export const getInfiniteExperimentAuditLogQuery = createInfiniteQueryOptions(postAuditLogEntries);

export const useUploadStratifiedSampling = createMutationHook(internalPostExperimentStratifiedSampling);

export const useDeleteStratifiedSampling = createMutationHook(internalDeleteExperimentStratifiedSampling);

export const useGetStratifiedSampling = createMutationHook(internalGetExperimentStratifiedSampling);
